import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getWithdrawalLimit } from '../../api/walletApi';

const cyan = '#00e5ff';
const orange = '#ffaa44';
const cardDark = {
  background: 'radial-gradient(ellipse at 100% 0%, rgba(0,229,255,0.06) 0%, #0d0d0d 65%)',
  border: '1px solid rgba(0,229,255,0.15)',
  color: '#fff',
};
const th = { color: cyan, fontWeight: 600, fontSize: 13, padding: '12px 20px', border: 'none', whiteSpace: 'nowrap' };
const td = { fontSize: 13, padding: '14px 20px', border: 'none', textAlign: 'center', whiteSpace: 'nowrap' };

/**
 * Build a lookup: renewalCount → (planIdStr → row)
 * Returns { maxRenewal, renewalCounts, planColumns, byRenewal }
 */
const buildTableData = (rows) => {
  if (!rows || rows.length === 0) return { renewalCounts: [], planColumns: [], byRenewal: {} };

  // Unique ordered plan columns (preserve API order = sorted by amount)
  const seenPlans = new Map();
  const planColumns = [];
  for (const r of rows) {
    const key = r.planId.toString ? r.planId.toString() : String(r.planId);
    if (!seenPlans.has(key)) {
      seenPlans.set(key, true);
      planColumns.push({ planId: key, planName: r.planName, amount: r.amount });
    }
  }

  // Unique sorted renewal rounds
  const renewalSet = new Set(rows.map((r) => r.renewalCount ?? 0));
  const renewalCounts = [...renewalSet].sort((a, b) => a - b);

  // byRenewal[r][planIdStr] = row
  const byRenewal = {};
  for (const r of renewalCounts) byRenewal[r] = {};
  for (const row of rows) {
    const r = row.renewalCount ?? 0;
    const key = row.planId.toString ? row.planId.toString() : String(row.planId);
    byRenewal[r][key] = row;
  }

  return { renewalCounts, planColumns, byRenewal };
};

const WithdrawalLimits = () => {
  const navigate = useNavigate();
  const [limitData, setLimitData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getWithdrawalLimit()
      .then((res) => setLimitData(res.data))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load withdrawal limits.'))
      .finally(() => setLoading(false));
  }, []);

  const { renewalCounts, planColumns, byRenewal } = buildTableData(limitData?.slotBreakdown);

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', paddingBottom: 40 }}>
      <Container className="pt-4">
        <button
          onClick={() => navigate('/account-status')}
          style={{ background: 'none', border: 'none', color: cyan, fontSize: 13, cursor: 'pointer', marginBottom: 20, padding: 0 }}
        >
          ‹ Back to Account Status
        </button>

        <h5 style={{ color: '#fff', fontWeight: 700, marginBottom: 4 }}>Withdrawal Limits</h5>
        <div style={{ color: '#888', fontSize: 13, marginBottom: 24 }}>
          Your withdrawal limit grows as you activate slots and bring in active direct referrals.
          Each renewal round (R1, R2…) adds its own rows below the primary.
        </div>

        {error && (
          <div style={{ color: '#ff6666', background: 'rgba(200,50,50,0.12)', border: '1px solid rgba(200,50,50,0.3)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13 }}>
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: cyan }} />
          </div>
        ) : (
          <>
            {/* Summary cards */}
            <Row className="g-3 mb-4">
              {[
                { label: 'Total Limit',   value: limitData?.totalLimit,     color: orange },
                { label: 'Used Limit',    value: limitData?.usedLimit,      color: '#ff6666' },
                { label: 'Balance Limit', value: limitData?.availableLimit, color: '#66ff99' },
              ].map(({ label, value, color }) => (
                <Col xs={4} key={label}>
                  <Card style={cardDark} className="p-2 p-md-4 h-100">
                    <div style={{ color: '#888', fontSize: 11, marginBottom: 4 }}>{label}</div>
                    <div>
                      <span style={{ fontSize: 'clamp(14px, 4vw, 28px)', fontWeight: 800, color }}>{(value ?? 0).toFixed(2)}</span>
                      <span style={{ color: '#aaa', fontSize: 11, marginLeft: 4 }}>USDT</span>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Horizontal slot-wise breakdown — slots as columns, renewal rounds as row groups */}
            <div style={{ color: '#aaa', fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Slot-wise Limit Breakdown</div>
            <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(0,229,255,0.12)' }}>
              <Table responsive className="mb-0" style={{ background: 'transparent' }}>
                <thead>
                  <tr style={{ background: 'rgba(0,229,255,0.07)', borderBottom: '1px solid rgba(0,229,255,0.15)' }}>
                    <th style={{ ...th, whiteSpace: 'nowrap' }}>Slot</th>
                    {planColumns.map((p) => (
                      <th key={p.planId} style={{ ...th, textAlign: 'center' }}>{p.planName}</th>
                    ))}
                    <th style={{ ...th, textAlign: 'center', color: orange }}>Total</th>
                  </tr>
                </thead>

                <tbody>
                  {renewalCounts.map((r, rIdx) => {
                    const label = r === 0 ? 'Primary' : `R${r}`;
                    const labelColor = r === 0 ? cyan : orange;
                    const labelBg   = r === 0 ? 'rgba(0,229,255,0.08)' : 'rgba(255,170,68,0.08)';
                    const rowData   = byRenewal[r] || {};

                    const totalLimitForRound = planColumns.reduce((sum, p) => {
                      return sum + (rowData[p.planId]?.limitReceived || 0);
                    }, 0);

                    return [
                      /* Section header row — separates Primary / R1 / R2 */
                      rIdx > 0 && (
                        <tr key={`sep-${r}`}>
                          <td
                            colSpan={planColumns.length + 2}
                            style={{ background: 'rgba(255,255,255,0.02)', borderTop: '1px solid rgba(255,255,255,0.06)', padding: '4px 20px', border: 'none' }}
                          />
                        </tr>
                      ),

                      /* Upgrade row */
                      <tr key={`${r}-upgrade`} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        <td style={{ color: '#fff', fontSize: 13, fontWeight: 600, padding: '14px 20px', border: 'none', whiteSpace: 'nowrap' }}>
                          <span style={{
                            display: 'inline-block',
                            background: labelBg,
                            border: `1px solid ${labelColor}44`,
                            color: labelColor,
                            borderRadius: 4,
                            padding: '1px 7px',
                            fontSize: 10,
                            fontWeight: 700,
                            marginRight: 8,
                          }}>
                            {label}
                          </span>
                          Upgrade
                        </td>
                        {planColumns.map((p) => {
                          const cell = rowData[p.planId];
                          return (
                            <td key={p.planId} style={td}>
                              <span style={{ color: cell?.selfActive ? '#66ff99' : '#555' }}>
                                {cell ? `${cell.amount} USDT` : '—'}
                              </span>
                            </td>
                          );
                        })}
                        <td style={{ ...td, color: '#555' }}>—</td>
                      </tr>,

                      /* Direct Referrals row */
                      <tr key={`${r}-directs`} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        <td style={{ color: '#fff', fontSize: 13, fontWeight: 600, padding: '14px 20px', border: 'none', whiteSpace: 'nowrap' }}>
                          <span style={{
                            display: 'inline-block',
                            background: labelBg,
                            border: `1px solid ${labelColor}44`,
                            color: labelColor,
                            borderRadius: 4,
                            padding: '1px 7px',
                            fontSize: 10,
                            fontWeight: 700,
                            marginRight: 8,
                          }}>
                            {label}
                          </span>
                          Direct Referrals
                        </td>
                        {planColumns.map((p) => {
                          const cell = rowData[p.planId];
                          return (
                            <td key={p.planId} style={{ ...td, color: cyan }}>
                              {cell?.directCount >= 1 ? '1' : ''}
                            </td>
                          );
                        })}
                        <td style={{ ...td, color: cyan, fontWeight: 700 }}>
                          {planColumns.some((p) => (rowData[p.planId]?.directCount || 0) >= 1) ? '1' : ''}
                        </td>
                      </tr>,

                      /* Limit Received row */
                      <tr key={`${r}-limit`}>
                        <td style={{ color: '#fff', fontSize: 13, fontWeight: 600, padding: '14px 20px', border: 'none', whiteSpace: 'nowrap' }}>
                          <span style={{
                            display: 'inline-block',
                            background: labelBg,
                            border: `1px solid ${labelColor}44`,
                            color: labelColor,
                            borderRadius: 4,
                            padding: '1px 7px',
                            fontSize: 10,
                            fontWeight: 700,
                            marginRight: 8,
                          }}>
                            {label}
                          </span>
                          Limit Received
                        </td>
                        {planColumns.map((p) => {
                          const cell = rowData[p.planId];
                          return (
                            <td key={p.planId} style={{ ...td, color: cell?.limitReceived > 0 ? orange : '#555', fontWeight: cell?.limitReceived > 0 ? 700 : 400 }}>
                              {cell?.limitReceived > 0 ? `${cell.limitReceived.toFixed(2)} USDT` : '—'}
                            </td>
                          );
                        })}
                        <td style={{ ...td, color: totalLimitForRound > 0 ? orange : '#555', fontWeight: 800 }}>
                          {totalLimitForRound > 0 ? `${totalLimitForRound.toFixed(2)} USDT` : '—'}
                        </td>
                      </tr>,
                    ];
                  })}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </Container>
    </div>
  );
};

export default WithdrawalLimits;
